import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`).toLocaleString('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
};

/** Monitor de API Medilink: contador diario + últimas 10 sincronizaciones masivas. */
export function MedilinkMonitorCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/medilink/monitor`, { withCredentials: true });
      setData(data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresca cada 30s
    return () => clearInterval(t);
  }, [load]);

  const today = data?.today || { total: 0, ok: 0, rate_limited: 0, error: 0 };
  const syncs = data?.recent_bulk_syncs || [];

  return (
    <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="medilink-monitor-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
          <Activity size={24} className="text-[#2B4C3B]" />
          Monitor de API Medilink
        </CardTitle>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          size="sm"
          data-testid="monitor-refresh-btn"
          className="border-[#E8E5DD]"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Contador de HOY */}
        <div>
          <p className="text-xs text-[#6A736B] uppercase tracking-wider font-semibold mb-2">
            Peticiones HOY ({today.date || 'sin datos'})
          </p>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-[#F3EBE1] rounded-lg p-3">
              <p className="text-xs text-[#6A736B]">Total</p>
              <p className="text-2xl font-bold text-[#1A1C18]" data-testid="monitor-today-total">
                {today.total}
              </p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle2 size={12} /> Exitosas
              </p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="monitor-today-ok">
                {today.ok}
              </p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-700 flex items-center gap-1">
                <AlertTriangle size={12} /> Rate limit (429)
              </p>
              <p className="text-2xl font-bold text-amber-700" data-testid="monitor-today-429">
                {today.rate_limited}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-red-700 flex items-center gap-1">
                <XCircle size={12} /> Errores
              </p>
              <p className="text-2xl font-bold text-red-700" data-testid="monitor-today-error">
                {today.error}
              </p>
            </div>
          </div>
        </div>

        {/* Historial de sincronizaciones masivas */}
        <div>
          <p className="text-xs text-[#6A736B] uppercase tracking-wider font-semibold mb-2">
            Últimas 10 sincronizaciones masivas
          </p>
          {syncs.length === 0 ? (
            <p className="text-sm text-[#6A736B] italic bg-[#FDFBF7] border border-[#E8E5DD] rounded-lg p-4 text-center">
              Aún no hay sincronizaciones masivas registradas.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="monitor-syncs-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#6A736B] border-b border-[#E8E5DD]">
                    <th className="py-2 px-2">Inicio</th>
                    <th className="py-2 px-2">Estado</th>
                    <th className="py-2 px-2 text-right">Procesados</th>
                    <th className="py-2 px-2 text-right">Actualizados</th>
                    <th className="py-2 px-2 text-right">Fallidos</th>
                    <th className="py-2 px-2 text-right">429</th>
                  </tr>
                </thead>
                <tbody>
                  {syncs.map((s) => {
                    const has429 = s.rate_limited_pauses > 0;
                    return (
                      <tr
                        key={s.job_id}
                        className="border-b border-[#F3EBE1] last:border-0"
                        data-testid={`monitor-sync-row-${s.job_id}`}
                      >
                        <td className="py-2 px-2 text-[#1A1C18]">{fmt(s.started_at)}</td>
                        <td className="py-2 px-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              s.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : s.status === 'running'
                                ? 'bg-blue-100 text-blue-700 animate-pulse'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-[#1A1C18]">
                          {s.processed}/{s.total}
                        </td>
                        <td className="py-2 px-2 text-right text-emerald-700 font-medium">
                          {s.updated}
                        </td>
                        <td className={`py-2 px-2 text-right font-medium ${s.failed > 0 ? 'text-red-700' : 'text-[#6A736B]'}`}>
                          {s.failed}
                        </td>
                        <td className={`py-2 px-2 text-right font-medium ${has429 ? 'text-amber-700' : 'text-[#6A736B]'}`}>
                          {has429 ? `⚠ ${s.rate_limited_pauses}` : '0'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
